import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Pencil,
  CheckCircle,
  Link2,
  ExternalLink,
  Warehouse,
  RefreshCw,
  Split,
  Receipt,
  Loader2,
  X,
  Factory,
  Package,
} from 'lucide-react';
import Layout from '../components/Layout';
import SoPoSplittingDialog from '../components/SoPoSplittingDialog';
import UnifiedConversionDialog from '../components/UnifiedConversionDialog';
import LinkPODialog from '../components/LinkPODialog';
import LinkProductionDialog from '../components/LinkProductionDialog';
import LinkedOrdersSection from '../components/LinkedOrdersSection';
import FileAttachmentManager from '../components/FileAttachmentManager';
import CreateOutboundDialog from '../components/warehouse/CreateOutboundDialog';
import ProductInventoryDrawer from '../components/ProductInventoryDrawer';
import FrozenStockDrawer from '../components/FrozenStockDrawer';
import WarehouseMovementDrawer from '../components/WarehouseMovementDrawer';
import ProductTable from '../components/ProductTable';
import TemplateSelector from '../components/TemplateSelector';
import BulkDownloadProductAttachmentsButton from '../components/BulkDownloadProductAttachmentsButton';
import excelExportService from '../services/excelExportService';
import salesService from '../services/salesService';
import authService from '../services/authService';
import purchaseService from '../services/purchaseService';
import productionService from '../services/productionService';
import { calculatePricingData } from '../utils/taxCalculator';

const SalesDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.orderData || null);
  const [loading, setLoading] = useState(!location.state?.orderData);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', order: null });
  const [splittingDialog, setSplittingDialog] = useState({ open: false });
  const [unifiedConversionOpen, setUnifiedConversionOpen] = useState(false);
  const [linkPODialog, setLinkPODialog] = useState({ open: false });
  const [unlinkDialog, setUnlinkDialog] = useState({ open: false, po: null });
  const [linkPRDDialog, setLinkPRDDialog] = useState({ open: false });
  const [unlinkPRDDialog, setUnlinkPRDDialog] = useState({ open: false, prd: null });
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [outboundDialogOpen, setOutboundDialogOpen] = useState(false);
  const [linkedPOs, setLinkedPOs] = useState([]);
  const [linkedPRDs, setLinkedPRDs] = useState([]);

  // Product Inventory Drawer state
  const [inventoryDrawer, setInventoryDrawer] = useState({
    open: false,
    productCode: null,
    productName: null
  });

  // Frozen (reserved) Stock Drawer state
  const [reservedDrawer, setReservedDrawer] = useState({
    open: false,
    productCode: null,
    productName: null,
    lineItemId: null
  });

  // Warehouse Movement Drawer state
  const [movementDrawer, setMovementDrawer] = useState({
    open: false,
    movementType: null,
    sourceId: null,
    sourceReference: null,
  });

  const [executionProgress, setExecutionProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);

  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const user = authService.getCurrentUser();
    setUserRole(user?.role || '');
  }, []);

  const loadOrder = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const response = await salesService.getSalesOrder(id);
      setOrder(response);
    } catch (err) {
      setError(err.response?.data?.error || '加载订单详情失败');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadExecutionProgress = useCallback(async () => {
    if (!id) return;

    try {
      setLoadingProgress(true);
      const response = await salesService.getExecutionProgress(id);
      setExecutionProgress(response);
    } catch (err) {
      console.error('Error loading execution progress:', err);
    } finally {
      setLoadingProgress(false);
    }
  }, [id]);

  // Derive linked POs and PRDs from executionProgress and order data
  useEffect(() => {
    if (!executionProgress && !order) return;

    // Extract linked POs from executionProgress (per-product linked_pos)
    const mergedPOs = new Map();
    if (executionProgress?.per_product) {
      Object.values(executionProgress.per_product).forEach(product => {
        if (product.linked_pos) {
          product.linked_pos.forEach(po => {
            if (!mergedPOs.has(po.po_number)) {
              mergedPOs.set(po.po_number, {
                _id: po.po_id,
                purchase_order_id: po.po_number,
                supplier_name: po.supplier_name,
                status: po.status,
                total_products: po.total_products,
                created_at: po.created_at
              });
            }
          });
        }
      });
    }

    // Merge with order.linked_purchase_orders to fill missing fields
    // and handle cases where so_po_mappings don't have matching so_line_item_id
    if (order?.linked_purchase_orders) {
      order.linked_purchase_orders.forEach(po => {
        const existing = mergedPOs.get(po.purchase_order_id);
        if (existing) {
          // Enrich missing fields from order data
          mergedPOs.set(po.purchase_order_id, {
            ...existing,
            supplier_name: existing.supplier_name || po.supplier_name,
            status: existing.status || po.status,
            total_products: existing.total_products ?? po.total_products,
            created_at: existing.created_at || po.created_at
          });
        } else {
          mergedPOs.set(po.purchase_order_id, {
            _id: po._id || po.purchase_order_id,
            purchase_order_id: po.purchase_order_id,
            supplier_name: po.supplier_name,
            status: po.status,
            total_products: po.total_products,
            created_at: po.created_at
          });
        }
      });
    }
    setLinkedPOs(Array.from(mergedPOs.values()));

    // Extract linked production orders
    const allLinkedPRDs = [];
    if (executionProgress?.per_product) {
      Object.values(executionProgress.per_product).forEach(product => {
        if (product.production && product.production.linked_prds) {
          product.production.linked_prds.forEach(prd => {
            if (!allLinkedPRDs.find(p => p.production_order_id === prd.production_order_id)) {
              allLinkedPRDs.push(prd);
            }
          });
        }
      });
    }
    setLinkedPRDs(allLinkedPRDs);
  }, [executionProgress, order]);

  const computeProcurementSummary = (executionProgress) => {
    if (!executionProgress?.per_product) return null;

    let totalRequired = 0;
    let totalAllocated = 0;
    let totalReceived = 0;

    Object.values(executionProgress.per_product).forEach(product => {
      totalRequired += product.needs_procurement || 0;
      totalAllocated += product.allocated_to_po || 0;
      totalReceived += product.received_qty || 0;
    });

    const allocatedPct = totalRequired > 0 ? (totalAllocated / totalRequired) * 100 : 0;
    const receivedPct = totalRequired > 0 ? (totalReceived / totalRequired) * 100 : 0;

    let status = 'not_started';
    if (totalReceived >= totalRequired && totalRequired > 0) {
      status = 'complete';
    } else if (totalAllocated > 0 || totalReceived > 0) {
      status = 'partial';
    }

    return {
      status,
      totals: {
        required_qty: totalRequired,
        allocated_qty: totalAllocated,
        received_qty: totalReceived
      },
      allocated_completion_pct: allocatedPct,
      received_completion_pct: receivedPct
    };
  };

  useEffect(() => {
    loadOrder();
    loadExecutionProgress();
  }, [loadOrder, loadExecutionProgress]);

  const handleAction = (action) => {
    if (action === 'edit') {
      navigate(`/sales/${id}/edit`);
    } else if (action === 'ship' || action === 'confirm' || action === 'invoice') {
      setConfirmDialog({ open: true, action, order });
    } else if (action === 'convert' || action === 'split') {
      setSplittingDialog({ open: true });
    }
  };

  const handleConfirmAction = async () => {
    const { action, order: orderData } = confirmDialog;
    setConfirmDialog({ open: false, action: '', order: null });

    try {
      if (action === 'ship') {
        await salesService.shipOrder(orderData._id);
        setOrder(prev => ({ ...prev, status: 'shipped' }));
      } else if (action === 'confirm') {
        await salesService.confirmSalesOrder(orderData._id);
        setOrder(prev => ({ ...prev, status: 'confirmed' }));
      } else if (action === 'invoice') {
        await salesService.invoiceOrder(orderData._id);
        setOrder(prev => ({ ...prev, status: 'invoiced' }));
      }
    } catch (err) {
      const actionText = action === 'ship' ? '发货' : action === 'invoice' ? '开票' : '确认';
      setError(err.response?.data?.error || `${actionText}订单失败`);
    }
  };

  const handleDeletePRD = async (orderId) => {
    try {
      await productionService.deleteProductionOrder(orderId);
      // Remove from linkedPRDs
      setLinkedPRDs(prev => prev.filter(prd => prd._id !== orderId));
      alert('工单已永久删除');
    } catch (err) {
      setError('删除工单失败: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    }
  };

  const handleUnlinkPRD = async () => {
    const prd = unlinkPRDDialog.prd;
    if (!prd) return;
    setUnlinkPRDDialog({ open: false, prd: null });
    try {
      await productionService.unlinkProductionOrderFromSales(prd._id);
      setLinkedPRDs(prev => prev.filter(p => p._id !== prd._id));
      alert(`工单 ${prd.production_order_id} 已取消关联`);
      await loadExecutionProgress();
    } catch (err) {
      setError('取消关联工单失败: ' + (err.response?.data?.error || err.message || 'Unknown error'));
    }
  };

  const handleSplittingComplete = (result) => {
    setSplittingDialog({ open: false });
    if (result.success) {
      const poIds = result.created_purchase_orders.map(po => po.purchase_order_id).join(', ');
      alert(`成功创建 ${result.created_purchase_orders.length} 个采购订单：${poIds}`);
      loadExecutionProgress();
    }
  };

  const handleTemplateSelected = async (template) => {
    try {
      const { blob, filename } = await excelExportService.exportUsingTemplate('sales', {
        template_id: template.template_id,
        sales_order_ids: [order._id]
      });

      excelExportService.downloadBlob(blob, filename);
    } catch (err) {
      setError('导出失败: ' + (err.message || 'Unknown error'));
    }
    setTemplateSelectorOpen(false);
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'default';
      case 'shipped': return 'secondary';
      case 'invoiced': return 'outline';
      case 'delivered': return 'default';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed': return 'bg-success/10 text-success border-success/30';
      case 'shipped': return 'bg-info/10 text-info border-info/30';
      case 'invoiced': return 'bg-secondary/10 text-secondary border-secondary/30';
      case 'delivered': return 'bg-primary/10 text-primary border-primary/30';
      case 'cancelled': return 'bg-destructive/10 text-destructive border-destructive/30';
      default: return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return '新建';
      case 'draft': return '草稿';
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'shipped': return '已发货';
      case 'delivered': return '已送达';
      case 'invoiced': return '已开票';
      case 'cancelled': return '已取消';
      case 'completed': return '已完成';
      default: return status || '未知';
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Layout title="销售订单详情" activeTab="sales">
        <div className="max-w-7xl mx-auto py-8 px-4">
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout title="销售订单详情" activeTab="sales">
        <div className="max-w-7xl mx-auto py-8 px-4">
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground mb-4">
              订单未找到
            </p>
            <Button onClick={() => navigate('/sales')}>
              返回销售列表
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const summary = computeProcurementSummary(executionProgress);

  // Derive tax-aware display totals
  const pricingMode = order.pricing_mode || 'tax_exclusive';
  const taxRate = typeof order.tax_rate === 'number' ? order.tax_rate : 0.13;
  const pricing = calculatePricingData(order.total_price, pricingMode, taxRate);

  return (
    <Layout title="销售订单详情" activeTab="sales">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8 bg-muted/30 min-h-full">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/sales')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              销售订单详情
            </h1>
            <p className="text-sm text-muted-foreground">
              订单号：{order.sales_order_id}
            </p>
          </div>
        </div>

        {/* Order Information - Compact */}
        <Card className="border bg-card mb-4">
          <CardContent className="p-4">
            <h2 className="text-base font-semibold mb-3">订单信息</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">订单编号</p>
                <p className="font-medium truncate">{order.sales_order_id}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">客户名称</p>
                <p className="font-medium truncate">{order.client_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">状态</p>
                <Badge variant={getStatusVariant(order.status)} className={getStatusClass(order.status)}>
                  {getStatusLabel(order.status)}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">产品总数</p>
                <p className="font-medium">{order.total_products || order.products?.length || 0}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">订单日期</p>
                <p className="font-medium">{formatDate(order.order_date)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">价格栏</p>
                <p className="font-medium">{order.price_column || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">
                  {pricingMode === 'tax_exclusive' ? '合计金额（不含税）' : '合计金额（含税）'}
                </p>
                {authService.canViewSalesPrices() ? (
                  <>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(pricingMode === 'tax_exclusive' ? pricing.taxExclusivePrice : pricing.taxInclusivePrice)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {pricingMode === 'tax_exclusive' ? (
                        <>
                          含税：{formatCurrency(pricing.taxInclusivePrice)}
                          <span className="ml-2">税额：{formatCurrency(pricing.taxAmount)}</span>
                        </>
                      ) : (
                        <>
                          税前金额：{formatCurrency(pricing.taxExclusivePrice)}
                          <span className="ml-2">税额：{formatCurrency(pricing.taxAmount)}</span>
                        </>
                      )}
                    </p>
                  </>
                ) : (
                  <p className="text-lg font-bold text-muted-foreground">*** 无权查看 ***</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Procurement Status Section */}
        <Card className={cn("border bg-card mb-6", executionProgress && "border-info")}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                采购执行状态
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={loadExecutionProgress}
                disabled={loadingProgress}
              >
                {loadingProgress ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                刷新状态
              </Button>
            </div>

            {loadingProgress ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : summary ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Overall Status */}
                <div className="text-center p-4 bg-info/10 rounded-lg">
                  <p className="text-2xl mb-1">
                    {summary.status === 'complete' ? '✅' : summary.status === 'partial' ? '⏳' : '⏸️'}
                  </p>
                  <p className="text-xl font-bold text-primary">
                    {summary.status === 'complete' ? '已完成' : summary.status === 'partial' ? '进行中' : '未开始'}
                  </p>
                  {(Number(summary.totals?.allocated_qty || 0) >= Number(summary.totals?.required_qty || 0)) && Number(summary.totals?.received_qty || 0) === 0 && (
                    <p className="text-xs text-warning mt-1">已全部分配（待收货）</p>
                  )}
                  <p className="text-sm text-muted-foreground">总体状态</p>
                </div>

                {/* Progress Statistics */}
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">分配完成度</p>
                  <p className="text-xl font-bold text-primary">
                    {summary.allocated_completion_pct?.toFixed(1) || '0.0'}%
                  </p>
                  <p className="text-xs text-muted-foreground mb-2">
                    {summary.totals?.allocated_qty || 0} / {summary.totals?.required_qty || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">收货完成度</p>
                  <p className="text-xl font-bold text-success">
                    {summary.received_completion_pct?.toFixed(1) || '0.0'}%
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {summary.totals?.received_qty || 0} / {summary.totals?.required_qty || 0}
                  </p>
                </div>

                <div className="text-center p-4 bg-warning/10 rounded-lg">
                  <p className="text-xl font-bold text-info">{linkedPOs.length}</p>
                  <p className="text-sm text-muted-foreground">关联采购单</p>
                </div>

              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">此销售订单尚未关联任何采购订单</p>
                <p className="text-sm text-muted-foreground mt-2">
                  使用"转为采购订单"功能创建关联的采购订单
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Linked Orders Section - Unified Component */}
        <LinkedOrdersSection
          linkedPOs={linkedPOs}
          linkedPRDs={linkedPRDs}
          onUnlinkPO={(po) => setUnlinkDialog({ open: true, po })}
          onUnlinkPRD={(prd) => setUnlinkPRDDialog({ open: true, prd })}
          onDeletePRD={handleDeletePRD}
          onViewInbound={(po) => setMovementDrawer({
            open: true,
            movementType: 'inbound',
            sourceId: po._id,
            sourceReference: po.purchase_order_id,
          })}
        />

        {/* Unlink PO Confirmation Dialog */}
        <Dialog open={unlinkDialog.open} onOpenChange={(open) => !open && setUnlinkDialog({ open: false, po: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>确认操作</DialogTitle>
              <DialogDescription>
                确定要取消关联并删除采购订单「{unlinkDialog.po?.purchase_order_id}」吗？该操作不可撤销。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUnlinkDialog({ open: false, po: null })}>取消</Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  try {
                    await purchaseService.unlinkPurchaseOrderFromSales(unlinkDialog.po?._id || unlinkDialog.po?.purchase_order_id, true);
                    setUnlinkDialog({ open: false, po: null });
                    await loadExecutionProgress();
                    await loadOrder();
                  } catch (err) {
                    alert(err.response?.data?.error || '取消关联失败');
                  }
                }}
              >
                确认删除
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Unlink PRD Confirmation Dialog */}
        <Dialog open={unlinkPRDDialog.open} onOpenChange={(open) => !open && setUnlinkPRDDialog({ open: false, prd: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>确认操作</DialogTitle>
              <DialogDescription>
                确定要取消关联工单「{unlinkPRDDialog.prd?.production_order_id}」吗？取消关联后工单将恢复为未分配状态，不会删除工单本身。
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUnlinkPRDDialog({ open: false, prd: null })}>取消</Button>
              <Button
                variant="destructive"
                onClick={handleUnlinkPRD}
              >
                确认取消关联
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* International Export Status */}
        {order.is_international_trade && (
          <Card className="border-2 border-orange-500 mb-6">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <h2 className="text-lg font-semibold text-orange-500">🌍 国际出口订单</h2>
                <Badge className="bg-orange-500 text-white">出口管制</Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 border border-orange-500 rounded-lg bg-orange-50">
                  <p className="font-semibold mb-2">🚢 国际运输详情</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span>🇨🇳</span>
                    <span className="text-sm">中国</span>
                    <span className="text-orange-500 font-semibold">→</span>
                    <span className="text-sm font-medium">{order.destination_country || '目的地待确定'}</span>
                  </div>
                  <Badge variant="outline" className={cn(
                    order.export_compliance_status === 'restricted' ? 'border-destructive text-destructive' :
                    order.export_compliance_status === 'license_required' ? 'border-warning text-warning' :
                    order.export_compliance_status === 'approved' ? 'border-success text-success' :
                    'border-info text-info'
                  )}>
                    {order.export_compliance_status === 'restricted' ? '❌ 出口受限' :
                     order.export_compliance_status === 'license_required' ? '📄 需要许可证' :
                     order.export_compliance_status === 'approved' ? '✅ 出口批准' :
                     '⏳ 合规审查中'}
                  </Badge>
                </div>

                <div className="p-4 border border-info rounded-lg bg-info/5">
                  <p className="font-semibold mb-2">📋 出口文件状态</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2">✅ 商业发票</div>
                    <div className="flex items-center gap-2">✅ 装箱单</div>
                    <div className="flex items-center gap-2">⏳ 出口许可证</div>
                    <div className="flex items-center gap-2">❌ 原产地证明</div>
                  </div>
                </div>

                {userRole === 'admin' && (
                  <div className="md:col-span-2 p-4 border-2 border-secondary rounded-lg bg-secondary/20 relative">
                    <div className="absolute -top-2 -right-2 bg-secondary text-white px-2 py-1 rounded-full text-xs">🔒</div>
                    <p className="font-semibold text-secondary-foreground mb-4 uppercase tracking-wide">
                      🔧 国际贸易记录（仅管理员）
                    </p>
                    <div className="p-3 bg-warning/10 border border-warning rounded-lg mb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span>🔒</span>
                        <span className="font-semibold text-warning">需要管理员权限</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        此订单标记为国际出口。所有贸易记录和敏感数据仅限管理员用户访问。
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-semibold">终端客户：</p>
                        <p className="text-muted-foreground">{order.client_name}</p>
                        {order.destination_country && <p className="text-muted-foreground">{order.destination_country}</p>}
                      </div>
                      <div>
                        <p className="font-semibold">出口分类：</p>
                        <p className="text-muted-foreground">ECCN: 2B001.a (Dual-use)</p>
                        <p className="text-muted-foreground">HS Code: 8501.20.40</p>
                      </div>
                      <div>
                        <p className="font-semibold">出口许可证：</p>
                        <p className="text-muted-foreground">BIS-EXP-2024-{order.sales_order_id?.slice(-3) || '001'}</p>
                        <Badge variant="outline" className="border-warning text-warning mt-1">申请已提交</Badge>
                      </div>
                      <div>
                        <p className="font-semibold">合规状态：</p>
                        <p className="text-muted-foreground">✅ 筛查完成</p>
                        <p className="text-xs text-muted-foreground">最后检查：{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Products Table */}
        <Card className="border bg-card mb-6">
          <CardContent className="p-6">
            <ProductTable
              products={order.products || []}
              view="sales"
              readOnly={true}
              title={`产品清单 (${order.products?.length || 0} 项)`}
              canViewPrices={authService.canViewSalesPrices()}
              executionProgress={executionProgress}
              enableExpandableProgress={true}
              onInventoryClick={(product) => setInventoryDrawer({
                open: true,
                productCode: product.product_code || product.product_id,
                productName: product.name || product.product_name
              })}
              onReservedClick={(product) => setReservedDrawer({
                open: true,
                productCode: product.product_code || product.product_id,
                productName: product.name || product.product_name,
                lineItemId: product.line_item_id || String(product._id || '')
              })}
            />
          </CardContent>
        </Card>

        {/* File Attachments */}
        <FileAttachmentManager
          entityType="sales_orders"
          entityId={order?._id}
          title="销售订单附件"
          className="mb-6"
        />

        {/* Action Buttons */}
        <div className="flex justify-between items-center mt-6">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTemplateSelectorOpen(true)}>
              <ExternalLink className="h-4 w-4 mr-2" />
              导出到Excel
            </Button>
            <BulkDownloadProductAttachmentsButton
              entityType="sales_orders"
              entityId={order._id}
              entityName={order.sales_order_id}
            />
          </div>

          <div className="flex gap-2 flex-wrap justify-end">
            <Button variant="outline" onClick={() => navigate('/sales')}>返回订单列表</Button>
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => handleAction('edit')}>
                <Pencil className="h-4 w-4 mr-2" />
                编辑订单
              </Button>
            )}
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => handleAction('split')}>
                <Split className="h-4 w-4 mr-2" />
                生成采购订单
              </Button>
            )}
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => setUnifiedConversionOpen(true)}>
                <Factory className="h-4 w-4 mr-2" />
                生成供应单(采购/生产)
              </Button>
            )}
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => setLinkPODialog({ open: true })}>
                <Link2 className="h-4 w-4 mr-2" />
                关联现有采购单
              </Button>
            )}
            {order.status !== 'invoiced' && (
              <Button variant="outline" onClick={() => setLinkPRDDialog({ open: true })}>
                <Factory className="h-4 w-4 mr-2" />
                关联现有工单
              </Button>
            )}
            {order.status === 'confirmed' && (
              <Button variant="outline" className="text-warning border-warning" onClick={() => setOutboundDialogOpen(true)}>
                <Warehouse className="h-4 w-4 mr-2" />
                创建出库单
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setMovementDrawer({
                open: true,
                movementType: 'outbound',
                sourceId: order._id,
                sourceReference: order.sales_order_id,
              })}
            >
              <Package className="h-4 w-4 mr-2" />
              出库记录
            </Button>
            {order.status !== 'confirmed' && order.status !== 'shipped' && order.status !== 'delivered' && order.status !== 'invoiced' && (
              <Button className="bg-success hover:bg-success/90 text-white" onClick={() => handleAction('confirm')}>
                <CheckCircle className="h-4 w-4 mr-2" />
                确认订单
              </Button>
            )}
            {order.status === 'shipped' && (
              <Button variant="secondary" onClick={() => handleAction('invoice')}>
                <Receipt className="h-4 w-4 mr-2" />
                已开发票
              </Button>
            )}
          </div>
        </div>

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', order: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmDialog.action === 'ship' ? '确认发货订单' :
                 confirmDialog.action === 'invoice' ? '确认已开发票' : '确认订单'}
              </DialogTitle>
              <DialogDescription>
                {confirmDialog.action === 'ship'
                  ? `您确定要发货订单 ${confirmDialog.order?.sales_order_id} 吗？`
                  : confirmDialog.action === 'invoice'
                    ? `您确定已为订单 ${confirmDialog.order?.sales_order_id} 开具发票吗？这将标记订单为已开票状态。`
                    : `您确定要确认订单 ${confirmDialog.order?.sales_order_id} 吗？这将标记为准备处理。`
                }
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, action: '', order: null })}>取消</Button>
              <Button onClick={handleConfirmAction}>确认</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* SO-PO Splitting Dialog */}
        <SoPoSplittingDialog
          open={splittingDialog.open}
          onClose={() => setSplittingDialog({ open: false })}
          onConvert={handleSplittingComplete}
          salesOrderId={order?._id}
          salesOrderData={order}
        />

        <UnifiedConversionDialog
          open={unifiedConversionOpen}
          onClose={() => setUnifiedConversionOpen(false)}
          salesOrderId={order?._id}
          onSuccess={(result) => {
            const messages = [];
            if (result.purchase_orders_created > 0) {
              messages.push(`${result.purchase_orders_created} 个采购订单`);
            }
            if (result.production_orders_created > 0) {
              messages.push(`${result.production_orders_created} 个生产工单`);
            }
            if (result.frozen_stock_count > 0) {
              messages.push(`${result.frozen_stock_count} 个冻结申请（待仓库审核）`);
            }
            
            if (messages.length === 0) {
              alert('操作成功');
            } else {
              alert(`成功创建: ${messages.join(', ')}`);
            }
            loadExecutionProgress();
          }}
        />

        {/* Link Existing PO Dialog */}
        <LinkPODialog
          open={linkPODialog.open}
          onClose={() => setLinkPODialog({ open: false })}
          onLinked={(result) => {
            setLinkPODialog({ open: false });
            alert(`成功关联 ${result.mappings_created || 0} 条采购订单映射记录`);
            loadExecutionProgress();
          }}
          salesOrderId={order?._id}
          salesOrderData={order}
        />

        {/* Link Existing Production Order Dialog */}
        <LinkProductionDialog
          open={linkPRDDialog.open}
          onClose={() => setLinkPRDDialog({ open: false })}
          onLinked={(result) => {
            setLinkPRDDialog({ open: false });
            alert(`成功关联 ${result.linked_count || 0} 个工单`);
            loadExecutionProgress();
          }}
          salesOrderId={order?._id}
          salesOrderData={order}
        />

        {/* Template Selector Dialog */}
        <TemplateSelector
          open={templateSelectorOpen}
          onClose={() => setTemplateSelectorOpen(false)}
          onSelect={handleTemplateSelected}
          entityType="sales"
          language="cn"
          title="选择销售订单导出模板"
        />

        {/* Create Outbound Dialog */}
        <CreateOutboundDialog
          open={outboundDialogOpen}
          onClose={() => setOutboundDialogOpen(false)}
          onSuccess={() => {
            setOutboundDialogOpen(false);
            alert('出库单创建成功！');
            loadExecutionProgress();
          }}
          salesOrderId={order?._id || order?.sales_order_id}
        />

        {/* Product Inventory Drawer */}
        <ProductInventoryDrawer
          open={inventoryDrawer.open}
          onClose={() => setInventoryDrawer({ open: false, productCode: null, productName: null })}
          productCode={inventoryDrawer.productCode}
          productName={inventoryDrawer.productName}
        />

        {/* Frozen (Reserved) Stock Drawer */}
        <FrozenStockDrawer
          open={reservedDrawer.open}
          onClose={() => setReservedDrawer({ open: false, productCode: null, productName: null, lineItemId: null })}
          productCode={reservedDrawer.productCode}
          productName={reservedDrawer.productName}
          frozenLocations={executionProgress?.per_product?.[reservedDrawer.lineItemId]?.frozen_locations || []}
          pendingFreezeQty={executionProgress?.per_product?.[reservedDrawer.lineItemId]?.pending_freeze_qty || 0}
          salesOrderId={order?._id}
          onUnfrozen={loadExecutionProgress}
        />

        {/* Warehouse Movement Drawer */}
        <WarehouseMovementDrawer
          open={movementDrawer.open}
          onClose={() => setMovementDrawer({ open: false, movementType: null, sourceId: null, sourceReference: null })}
          movementType={movementDrawer.movementType}
          sourceId={movementDrawer.sourceId}
          sourceReference={movementDrawer.sourceReference}
        />
      </div>
    </Layout>
  );
};

export default SalesDetailPage;
